import React, { useState, useEffect, useMemo } from 'react';
import {
  MemoryItem,
  CoupleSettings,
  TimeElapsed,
  DailyLetter,
  VisitorInfo,
  VisitorLog,
  NavVisibilitySettings,
  MyWebRequest,
  WebRequestStatus,
  BlockedIpEntry,
  GardenWateringState,
} from './types';
import {
  calculateTimeElapsed,
  clearAllLocalStorageAndIndexedDB,
} from './utils/storage';
import {
  subscribeToSettings,
  subscribeToMemories,
  subscribeToDailyLetters,
  subscribeToVisitorLogs,
  recordVisitorVisit,
  recordVisitorHeartbeat,
  deleteVisitorLogFromDb,
  clearAllVisitorLogsInDb,
  saveSettingsToDb,
  saveMemoryToDb,
  deleteMemoryFromDb,
  saveDailyLetterToDb,
  deleteDailyLetterFromDb,
  purgeAllSampleData,
  updateVisitorPreciseLocation,
  blockIpAddressInDb,
  unblockIpAddressInDb,
  subscribeToWebRequests,
  subscribeToUserWebRequests,
  getWebRequestByOrderCode,
  saveWebRequestToDb,
  updateUserWebRequestInDb,
  submitUserChangesForWebRequest,
  updateWebRequestStatusInDb,
  deleteWebRequestFromDb,
} from './services/firebaseDb';
import { detectVisitorInfo, getCachedVisitorIp, fetchFastPublicIp } from './utils/visitorDetector';
import { subscribeToMusicTracks } from './services/musicDb';
import { DEFAULT_SETTINGS } from './data/initialData';
import { InteractiveGarden } from './components/InteractiveGarden';
import { MediaFeed } from './components/MediaFeed';
import { DailyLettersView } from './components/DailyLettersView';
import { RomanticAnimationsView } from './components/RomanticAnimationsView';
import { MyWebView } from './components/MyWebView';
import { PersonalizedCoupleWebsite } from './components/PersonalizedCoupleWebsite';
import { UserWebAdminPanel } from './components/userWeb/UserWebAdminPanel';
import { UserWebPinModal } from './components/userWeb/UserWebPinModal';
import { UploadModal } from './components/UploadModal';
import { MediaLightbox } from './components/MediaLightbox';
import { BlockedIpScreen } from './components/BlockedIpScreen';
import { PublicAppDownloadPage } from './components/PublicAppDownloadPage';
import { WebMaintenanceScreen } from './components/WebMaintenanceScreen';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import {
  getClientAppVersion,
  setClientAppVersion,
  isInstalledAppRunning,
  STORAGE_KEY_JUST_UPDATED,
} from './utils/appVersion';
import {
  AdminPage,
  AdminSection,
  ADMIN_SECTION_PATHS,
  getAdminSectionFromPath,
} from './components/AdminPage';
import { MinimalistHeader } from './components/MinimalistHeader';
import { romanticAudio } from './utils/audio';
import { musicPlayer } from './utils/musicPlayer';
import { auth } from './firebase';
import {
  signOutAdmin,
  onAuthChange,
  isUserAdminSync,
  signInAdmin,
  signInPublicSession,
  getPersistedAdminUid,
  persistAdminUid,
} from './services/authService';
import { motion, AnimatePresence } from 'motion/react';
import {
  Flower2,
  Camera,
  Settings as SettingsIcon,
  Heart,
  Music,
  Volume2,
  Lock,
  Mail,
  Cloud,
  Trash2,
  Check,
  Sparkles,
  ShieldAlert,
  Ban,
  Globe,
  Quote,
  Copy,
  KeyRound,
  X,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

export type AppTab = 'garden' | 'blog' | 'letters' | 'animations' | 'myweb' | 'admin' | 'download-app' | 'reset-password';

// Helper to determine the active tab from browser URL pathname, search or hash
function getTabFromPath(pathname: string, search = '', hash = ''): AppTab {
  const s = (search || (typeof window !== 'undefined' ? window.location.search : '')).toLowerCase();
  const h = (hash || (typeof window !== 'undefined' ? window.location.hash : '')).toLowerCase();
  const clean = pathname.split('?')[0].split('#')[0].toLowerCase().replace(/\/+$/, '').trim();

  // Dedicated password recovery URL routing (Strictly OTP 6-Digit Code)
  if (
    clean === '/recuperar-contrasena' ||
    clean.startsWith('/recuperar-contrasena') ||
    clean === '/reset-password' ||
    clean.startsWith('/reset-password') ||
    clean === '/recuperar-password' ||
    clean === '/recuperar'
  ) {
    return 'reset-password';
  }

  // Si la URL contiene token de descarga o palabra clave de descarga en cualquier parte
  if (
    s.includes('token=') ||
    s.includes('download=') ||
    s.includes('descargar') ||
    h.includes('token=') ||
    h.includes('descargar') ||
    clean === '/descargar-app' ||
    clean === '/descargar' ||
    clean === '/app' ||
    clean === '/download-app' ||
    clean === '/instalar-app' ||
    clean.startsWith('/descargar-app') ||
    clean.startsWith('/descargar') ||
    clean.startsWith('/download-app') ||
    clean.startsWith('/app/descargar')
  ) {
    return 'download-app';
  }
  if (
    clean === '/admin' ||
    clean.startsWith('/admin/') ||
    clean === '/editor' ||
    clean === '/administrador' ||
    clean === '/panel'
  ) {
    return 'admin';
  }
  if (clean === '/album' || clean === '/recuerdos' || clean === '/fotos' || clean === '/galeria') {
    return 'blog';
  }
  if (
    clean === '/cartas' ||
    clean === '/cartas-diarias' ||
    clean === '/letters' ||
    clean === '/eventos' ||
    clean === '/events' ||
    clean === '/flores-amarillas' ||
    clean === '/cumpleanos'
  ) {
    return 'letters';
  }
  if (clean === '/frases' || clean === '/animaciones' || clean === '/animations') {
    return 'animations';
  }
  if (
    clean === '/mi-web' ||
    clean === '/miweb' ||
    clean === '/tu-web' ||
    clean === '/tuweb' ||
    clean === '/adquirir' ||
    clean === '/solicitud' ||
    clean === '/solicitudes' ||
    clean === '/myweb' ||
    clean === '/pedidos' ||
    clean.startsWith('/mi-web') ||
    clean.startsWith('/miweb')
  ) {
    return 'myweb';
  }
  return 'garden';
}

// Helper to get the canonical URL path for a given tab
function getPathForTab(tab: AppTab): string {
  switch (tab) {
    case 'garden':
      return '/jardin';
    case 'blog':
      return '/album';
    case 'letters':
      return '/cartas';
    case 'animations':
      return '/frases';
    case 'myweb':
      return '/mi-web';
    case 'admin':
      return '/admin';
    case 'download-app':
      return '/descargar-app';
    case 'reset-password':
      return '/reset-password';
  }
}

export default function App() {
  // Application State & URL routing
  const [activeTab, setActiveTab] = useState<AppTab>(() => {
    if (typeof window !== 'undefined') {
      return getTabFromPath(window.location.pathname, window.location.search, window.location.hash);
    }
    return 'garden';
  });

  // State populated 100% in real-time from Firestore Cloud Database
  const [settings, setSettings] = useState<CoupleSettings>(() => {
    try {
      if (typeof window !== 'undefined') {
        const cachedWatering = localStorage.getItem('couple_garden_watering');
        if (cachedWatering) {
          const parsed = JSON.parse(cachedWatering);
          return {
            ...DEFAULT_SETTINGS,
            gardenWatering: {
              ...DEFAULT_SETTINGS.gardenWatering,
              ...parsed,
            },
          };
        }
      }
    } catch {}
    return DEFAULT_SETTINGS;
  });
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [dailyLetters, setDailyLetters] = useState<DailyLetter[]>([]);
  const [visitorLogs, setVisitorLogs] = useState<VisitorLog[]>([]);
  const [webRequests, setWebRequests] = useState<MyWebRequest[]>([]);
  const [activeCustomWebRequest, setActiveCustomWebRequest] = useState<MyWebRequest | null>(null);
  const [customWebOrderCode, setCustomWebOrderCode] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('web') || params.get('sitio') || params.get('site');
      if (code) {
        const trimmed = code.trim();
        try {
          sessionStorage.setItem('current_active_web_code', trimmed);
        } catch {}
        return trimmed;
      }
      // If no query string, recover session if navigating within a custom web
      try {
        const stored = sessionStorage.getItem('current_active_web_code');
        if (stored && window.location.pathname !== '/mi-web') {
          return stored.trim();
        }
      } catch {}
    }
    return null;
  });

  const [currentVisitor, setCurrentVisitor] = useState<VisitorInfo | null>(null);
  const [isPlayingMusic, setIsPlayingMusic] = useState(false);
  const [isFirebaseConnected, setIsFirebaseConnected] = useState(false);
  
  // Database status
  const [isWebRequestsLoaded, setIsWebRequestsLoaded] = useState(false);

  // Automatically lookup and activate personalized couple website if URL query present
  const [customWebNotFound, setCustomWebNotFound] = useState(false);
  useEffect(() => {
    if (!customWebOrderCode) return;
    const cleanCode = customWebOrderCode.trim().replace(/^#/, '').toUpperCase();
    const found = webRequests.find(
      (r) =>
        r.orderCode?.replace(/^#/, '').toUpperCase() === cleanCode ||
        r.id === customWebOrderCode
    );
    if (found) {
      setActiveCustomWebRequest(found);
      setCustomWebNotFound(false);
      try {
        sessionStorage.setItem('current_active_web_code', found.orderCode || cleanCode);
      } catch {}
    } else {
      getWebRequestByOrderCode(cleanCode).then((req) => {
        if (req) {
          setActiveCustomWebRequest(req);
          setCustomWebNotFound(false);
          try {
            sessionStorage.setItem('current_active_web_code', req.orderCode || cleanCode);
          } catch {}
        } else if (isWebRequestsLoaded) {
          setCustomWebNotFound(true);
        }
      });
    }
  }, [customWebOrderCode, webRequests, isWebRequestsLoaded]);

  // Current Firebase Auth user state (Authoritative)
  const [currentAuthUser, setCurrentAuthUser] = useState<any>(() => auth.currentUser);

  // Initial check to avoid flickering/dropping session on page reloads
  const [isAuthInitializing, setIsAuthInitializing] = useState<boolean>(() => {
    return Boolean(getPersistedAdminUid() && !auth.currentUser);
  });

  // Admin authentication (Authoritative Firebase Authentication state)
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(() => {
    if (auth.currentUser && isUserAdminSync(auth.currentUser)) return true;
    return Boolean(getPersistedAdminUid());
  });

  // Keep admin authentication state synchronized with Firebase Auth token Custom Claims
  useEffect(() => {
    const unsubscribe = onAuthChange((user, isAdmin) => {
      setCurrentAuthUser(user);
      setIsAdminAuthenticated(isAdmin);
      setIsAuthInitializing(false);
    });
    return () => unsubscribe();
  }, []);

  // User Web Personal Admin Authentication and PIN states (pure in-memory state, zero sessionStorage reliance)
  const [isUserWebAdminAuth, setIsUserWebAdminAuth] = useState<boolean>(false);
  const setUserWebAdminAuth = (auth: boolean) => {
    setIsUserWebAdminAuth(auth);
  };
  const [isUserWebPinModalOpen, setIsUserWebPinModalOpen] = useState(false);

  // Scoped settings for User Web when active - 100% clean slate, zero leakage from original web
  const effectiveSettings: CoupleSettings = useMemo(() => {
    if (!activeCustomWebRequest) return settings;
    const custom = activeCustomWebRequest.customSettings || {};
    return {
      partner1Name:
        custom.partner1Name ||
        activeCustomWebRequest.applicantName ||
        'Él',
      partner2Name:
        custom.partner2Name ||
        activeCustomWebRequest.partnerName ||
        'Ella',
      partner1Title: custom.partner1Title || 'El Novio',
      partner2Title: custom.partner2Title || 'La Novia',
      partner1BirthDate: custom.partner1BirthDate || '',
      partner2BirthDate: custom.partner2BirthDate || '',
      partner1BloodType: custom.partner1BloodType || 'O+',
      partner2BloodType: custom.partner2BloodType || 'O+',
      partner1Avatar: custom.partner1Avatar || '',
      partner2Avatar: custom.partner2Avatar || '',
      startDate:
        custom.startDate ||
        activeCustomWebRequest.anniversaryDate ||
        '',
      quote:
        custom.quote ||
        activeCustomWebRequest.dedication ||
        '',
      location:
        custom.location || '',
      theme: custom.theme || 'rose',
      specialSong:
        activeCustomWebRequest.specialSong || '',
      playlist:
        custom.playlist ||
        (activeCustomWebRequest.specialSong
          ? [
              {
                id: `song-${activeCustomWebRequest.id}`,
                title: activeCustomWebRequest.specialSong,
                artist: activeCustomWebRequest.applicantName || 'Nosotros',
                url: activeCustomWebRequest.specialSong,
                isSpecialTrack: true,
              },
            ]
          : []),
      tulipSettings:
        custom.tulipSettings ||
        activeCustomWebRequest.customTulips || {
          mode: 'all',
          allColor: 'golden',
          individualColors: ['golden', 'golden', 'golden', 'golden'],
        },
      gardenWatering:
        custom.gardenWatering ||
        activeCustomWebRequest.customWatering || {
          lastWateredAt: new Date().toISOString(),
          wateringIntervalDays: 3,
          wateredCount: 0,
          witheringEnabled: true,
        },
      loveNotes: custom.loveNotes || [],
      bannerPhrases: custom.bannerPhrases || [],
      navVisibility: {
        garden: custom.navVisibility?.garden ?? true,
        blog: custom.navVisibility?.blog ?? true,
        letters: custom.navVisibility?.letters ?? true,
        animations: custom.navVisibility?.animations ?? true,
        myWeb: false, // Strictly hide myWeb in custom web
      },
      musicEnabled: custom.musicEnabled ?? true,
      musicAutoplay: custom.musicAutoplay ?? false,
      musicVolume: custom.musicVolume ?? 0.7,
      musicLoopMode: custom.musicLoopMode || 'all',
      activeSongId: custom.activeSongId,
      webMaintenance: { isEnabled: false },
      appKillSwitch: { isEnabled: false },
    };
  }, [settings, activeCustomWebRequest]);

  // Real-time ticking counter (Date.now() baseline, updates every second without freezing)
  const [timeElapsed, setTimeElapsed] = useState<TimeElapsed>(() => {
    if (activeCustomWebRequest && !activeCustomWebRequest.customSettings?.startDate && !activeCustomWebRequest.anniversaryDate) {
      return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0, totalDays: 0 };
    }
    return calculateTimeElapsed(effectiveSettings.startDate || (activeCustomWebRequest ? '' : '2024-01-01'));
  });

  // Keep live ticking counter synchronized every second and on tab visibility/focus
  useEffect(() => {
    const updateTime = () => {
      if (
        activeCustomWebRequest &&
        !activeCustomWebRequest.customSettings?.startDate &&
        !activeCustomWebRequest.anniversaryDate
      ) {
        setTimeElapsed({ years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0, totalDays: 0 });
        return;
      }
      const targetStart = effectiveSettings.startDate || (activeCustomWebRequest ? '' : '2024-01-01');
      setTimeElapsed(calculateTimeElapsed(targetStart));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        updateTime();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', updateTime);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', updateTime);
    };
  }, [effectiveSettings.startDate, activeCustomWebRequest]);

  const effectiveMemories: MemoryItem[] = useMemo(() => {
    if (!activeCustomWebRequest) return memories;
    // Strictly custom user memories - no sample data from main site
    return activeCustomWebRequest.customMemories || [];
  }, [memories, activeCustomWebRequest]);

  const effectiveLetters: DailyLetter[] = useMemo(() => {
    if (!activeCustomWebRequest) return dailyLetters;
    // Strictly custom user letters - no sample letters from main site
    return activeCustomWebRequest.customLetters || [];
  }, [dailyLetters, activeCustomWebRequest]);

  // Modals state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [lightboxMemory, setLightboxMemory] = useState<MemoryItem | null>(null);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState<number>(0);

  // Version and update status tracking
  const [clientAppVersion, setClientAppVersionState] = useState<string>(() => getClientAppVersion());
  const [justUpdatedVersion, setJustUpdatedVersion] = useState<string | null>(null);
  const [isRunningInApp, setIsRunningInApp] = useState<boolean>(() => isInstalledAppRunning());

  useEffect(() => {
    setIsRunningInApp(isInstalledAppRunning());
  }, []);

  // Detect completed update from storage or URL query
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_JUST_UPDATED);
      if (stored) {
        sessionStorage.removeItem(STORAGE_KEY_JUST_UPDATED);
        setJustUpdatedVersion(stored);
        setClientAppVersionState(stored);
      }
    } catch {}

    if (typeof window !== 'undefined' && window.location.search.includes('updated=')) {
      try {
        const params = new URLSearchParams(window.location.search);
        const ver = params.get('v');
        if (ver) {
          setClientAppVersion(ver);
          setClientAppVersionState(ver);
          setJustUpdatedVersion(ver);
        }
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch {}
    }
  }, []);

  // Listen for version updates across tabs/windows via BroadcastChannel
  useEffect(() => {
    try {
      const bc = new BroadcastChannel('pareja_security_channel');
      bc.onmessage = (event) => {
        if (event.data?.type === 'APP_VERSION_UPDATED' && event.data.version) {
          setClientAppVersionState(event.data.version);
        }
      };
      return () => bc.close();
    } catch {}
  }, []);

  // Floating feedback notice for actions (publish/delete)
  const [toastNotice, setToastNotice] = useState<{ text: string; type: 'success' | 'danger' | 'info' } | null>(null);

  const showNotice = (text: string, type: 'success' | 'danger' | 'info' = 'success') => {
    setToastNotice({ text, type });
    setTimeout(() => {
      setToastNotice((prev) => (prev?.text === text ? null : prev));
    }, 4000);
  };

  // Active sub-route in Admin panel (/admin/visitante, /admin/recuerdos, etc.)
  const [adminSection, setAdminSection] = useState<AdminSection>(() => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      return getAdminSectionFromPath(window.location.pathname);
    }
    return 'dashboard';
  });

  // Synchronize initial URL path on first mount
  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    const initialTab = getTabFromPath(currentPath, currentSearch, currentHash);
    setActiveTab(initialTab);
    if (initialTab === 'admin') {
      setAdminSection(getAdminSectionFromPath(currentPath));
    }

    if (initialTab !== 'download-app' && (currentPath === '/' || currentPath === '')) {
      const activeCode = customWebOrderCode;
      const search = currentSearch || (activeCode ? `?web=${encodeURIComponent(activeCode)}` : '');
      window.history.replaceState(null, '', `${getPathForTab(initialTab)}${search}`);
    }
  }, []);

  // Listen to browser Back/Forward navigation (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const currentHash = window.location.hash;
      const tab = getTabFromPath(currentPath, currentSearch, currentHash);

      const params = new URLSearchParams(currentSearch);
      const urlCode = params.get('web') || params.get('sitio') || params.get('site');
      if (urlCode && urlCode !== customWebOrderCode) {
        setCustomWebOrderCode(urlCode.trim());
      }

      setActiveTab(tab);
      if (tab === 'admin') {
        setAdminSection(getAdminSectionFromPath(currentPath));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [activeTab, customWebOrderCode]);

  // Admin sub-section navigation handler
  const handleAdminSectionChange = (section: AdminSection) => {
    setAdminSection(section);
    const targetPath = ADMIN_SECTION_PATHS[section] || '/admin';
    const activeCode = activeCustomWebRequest?.orderCode || customWebOrderCode;
    let newUrl = targetPath;
    if (activeCode) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('web', activeCode);
      newUrl = `${targetPath}?${searchParams.toString()}`;
    }
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.pushState(null, '', newUrl);
    }
  };

  // Navigation helper that updates both tab state and browser URL path
  const handleNavigate = (tab: AppTab) => {
    // Strictly prevent user custom webs from navigating to 'myweb' (Mi Web)
    if (activeCustomWebRequest && tab === 'myweb') {
      tab = 'garden';
    }
    if (activeCustomWebRequest && tab === 'admin') {
      if (!isUserWebAdminAuth) {
        setIsUserWebPinModalOpen(true);
        return;
      }
    }
    setActiveTab(tab);
    let targetPath = getPathForTab(tab);
    if (tab === 'admin') {
      targetPath = activeCustomWebRequest ? '/admin' : (ADMIN_SECTION_PATHS[adminSection] || '/admin');
    }

    // Maintain custom web query parameter (?web=...) across navigation
    const activeCode = activeCustomWebRequest?.orderCode || customWebOrderCode;
    let newUrl = targetPath;
    if (activeCode) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('web', activeCode);
      newUrl = `${targetPath}?${searchParams.toString()}`;
    }
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.pushState(null, '', newUrl);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Nav visibility configuration derived from settings
  const navVisibility = useMemo<NavVisibilitySettings>(() => {
    const isMyWebActive =
      settings.navVisibility?.myWeb !== false &&
      (settings.navVisibility as any)?.myweb !== false;

    return {
      garden: settings.navVisibility?.garden !== false,
      blog: settings.navVisibility?.blog !== false,
      letters: settings.navVisibility?.letters !== false,
      animations: settings.navVisibility?.animations !== false,
      myWeb: isMyWebActive,
      myweb: isMyWebActive,
    };
  }, [settings.navVisibility]);

  // If user is currently on a hidden tab and not in admin, automatically fallback to first visible tab
  useEffect(() => {
    if (activeTab === 'admin') return;
    // Direct navigation to 'myweb' is always permitted so visitors/couples are never blocked
    if (activeTab === 'myweb') return;
    // Direct navigation to download app page is always permitted
    if (activeTab === 'download-app') return;

    const isVisible = Boolean(navVisibility[activeTab as keyof NavVisibilitySettings]);
    if (!isVisible) {
      const order: AppTab[] = ['garden', 'blog', 'letters', 'animations', 'myweb'];
      const firstActive = order.find((t) => {
        if (t === 'myweb') {
          return navVisibility.myWeb !== false && navVisibility.myweb !== false;
        }
        return Boolean(navVisibility[t as keyof NavVisibilitySettings]);
      });
      if (firstActive && firstActive !== activeTab) {
        handleNavigate(firstActive);
      }
    }
  }, [navVisibility, activeTab]);

  // Detected Profile based on authenticated account and settings
  const detectedProfile = useMemo<'novio' | 'novia'>(() => {
    if (!currentAuthUser || currentAuthUser.isAnonymous) {
      return 'novio';
    }
    const uid = currentAuthUser.uid;
    const email = (currentAuthUser.email || '').toLowerCase().trim();

    // 1. Authoritative UID Match
    if (settings.partner2Uid && uid === settings.partner2Uid) {
      return 'novia';
    }
    if (settings.partner1Uid && uid === settings.partner1Uid) {
      return 'novio';
    }

    // 2. Authoritative Email Match
    const p2Email = (settings.partner2Email || '').toLowerCase().trim();
    if (p2Email && email === p2Email) {
      return 'novia';
    }
    const p1Email = (settings.partner1Email || '').toLowerCase().trim();
    if (p1Email && email === p1Email) {
      return 'novio';
    }

    // 3. Email heuristics
    if (
      email.includes('novia') ||
      email.includes('ella') ||
      (settings.partner2Name && email.includes(settings.partner2Name.toLowerCase()))
    ) {
      return 'novia';
    }

    return 'novio';
  }, [currentAuthUser, settings]);

  // Active Admin Profile: strictly and immutably derived from the authenticated Firebase user session.
  // Profile switching is completely disabled: Novio and Novia are 100% independent accounts.
  const activeAdminProfile: 'novio' | 'novia' = detectedProfile;

  // Handle successful Admin authentication
  const handleAdminAuthSuccess = (_profile?: 'novio' | 'novia') => {
    setIsAdminAuthenticated(true);
    setCurrentAuthUser(auth.currentUser);
    if (auth.currentUser) {
      persistAdminUid(auth.currentUser.uid);
    }
    romanticAudio.playBloomSuccess();
  };

  // Handle Admin Logout: To change perspective, the user must log out and sign in with the other account
  const handleLogoutAdmin = async () => {
    setIsAdminAuthenticated(false);
    setCurrentAuthUser(null);
    persistAdminUid(null);
    try {
      await signOutAdmin();
    } catch {}
    romanticAudio.playChime();
  };

  // Cached IP from storage for instantaneous block detection without network latency
  const [cachedVisitorIp, setCachedVisitorIp] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const fast = (window as any).__FAST_VISITOR_IP__;
      if (fast && typeof fast === 'string' && fast !== 'IP no detectada' && fast.trim()) {
        return fast.trim();
      }
    }
    return getCachedVisitorIp() || '';
  });

  // Effective visitor IP: priority to current detected visitor IP, falling back to cached IP
  const effectiveVisitorIp = useMemo(() => {
    if (currentVisitor?.ip && currentVisitor.ip !== 'IP no detectada') {
      return currentVisitor.ip.trim();
    }
    if (cachedVisitorIp && cachedVisitorIp !== 'IP no detectada') {
      return cachedVisitorIp.trim();
    }
    return '';
  }, [currentVisitor?.ip, cachedVisitorIp]);

  // Keep cached IP in sync whenever currentVisitor updates
  useEffect(() => {
    if (currentVisitor?.ip && currentVisitor.ip !== 'IP no detectada') {
      setCachedVisitorIp(currentVisitor.ip.trim());
    }
  }, [currentVisitor?.ip]);

  // 0ms Real-Time Security Channel: listens to cross-tab/local broadcasts for instant lockdown
  useEffect(() => {
    // 1. Fast IP resolution from index.html head
    const handleFastIp = (e: any) => {
      const ip = e?.detail?.ip;
      if (ip && ip !== 'IP no detectada') {
        setCachedVisitorIp(ip.trim());
      }
    };
    window.addEventListener('visitor-ip-resolved', handleFastIp);

    // 2. Early parallel IP resolution in ~80ms
    fetchFastPublicIp().then((fastIp) => {
      if (fastIp && fastIp !== 'IP no detectada') {
        setCachedVisitorIp(fastIp.trim());
      }
    });

    // 3. Instant cross-tab broadcast channel for 0ms immediate locks
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('pareja_security_channel');
      bc.onmessage = (event) => {
        if (event.data?.type === 'IP_BLOCKED') {
          const blocked = event.data.ip;
          const vid = event.data.visitorId;
          setSettings((prev) => {
            const current = Array.isArray(prev.blockedIps) ? [...prev.blockedIps] : [];
            const updatedIps = current.includes(blocked) ? current : [...current, blocked];
            const currentVids = Array.isArray(prev.blockedVisitorIds) ? [...prev.blockedVisitorIds] : [];
            const updatedVids = vid && !currentVids.includes(vid) ? [...currentVids, vid] : currentVids;
            return {
              ...prev,
              blockedIps: updatedIps,
              blockedVisitorIds: updatedVids,
            };
          });
        } else if (event.data?.type === 'IP_UNBLOCKED') {
          const unblocked = event.data.ip;
          const vid = event.data.visitorId;
          setSettings((prev) => ({
            ...prev,
            blockedIps: (prev.blockedIps || []).filter((x) => x !== unblocked),
            blockedVisitorIds: vid ? (prev.blockedVisitorIds || []).filter((x) => x !== vid) : prev.blockedVisitorIds,
          }));
        } else if (event.data?.type === 'APP_DISABLED' || event.data?.type === 'APP_ENABLED') {
          if (event.data.killSwitch) {
            setSettings((prev) => ({
              ...prev,
              appKillSwitch: event.data.killSwitch,
            }));
          }
        }
      };
    } catch {}

    return () => {
      window.removeEventListener('visitor-ip-resolved', handleFastIp);
      if (bc) bc.close();
    };
  }, []);

  // Determine if the current visitor IP has been blocked by the admin
  const isCurrentVisitorBlocked = useMemo(() => {
    if (isAdminAuthenticated) return false;
    const blockedList = settings.blockedIps;
    const blockedVids = settings.blockedVisitorIds;
    const hasBlockedIps = Array.isArray(blockedList) && blockedList.length > 0;
    const hasBlockedVids = Array.isArray(blockedVids) && blockedVids.length > 0;
    if (!hasBlockedIps && !hasBlockedVids) return false;

    // Check IP
    if (hasBlockedIps) {
      if (effectiveVisitorIp && blockedList.includes(effectiveVisitorIp)) return true;
      if (cachedVisitorIp && blockedList.includes(cachedVisitorIp)) return true;
      if (typeof window !== 'undefined') {
        const fastIp = (window as any).__FAST_VISITOR_IP__;
        if (fastIp && blockedList.includes(fastIp)) return true;
        const storedIp = localStorage.getItem('pareja_visitor_ip') || sessionStorage.getItem('pareja_visitor_ip');
        if (storedIp && blockedList.includes(storedIp)) return true;
        const lastBlocked = localStorage.getItem('pareja_last_blocked_ip');
        if (lastBlocked && blockedList.includes(lastBlocked)) return true;
      }
    }

    // Check Visitor ID / Device Fingerprint
    if (hasBlockedVids) {
      if (currentVisitor?.visitorId && blockedVids.includes(currentVisitor.visitorId)) return true;
      if (typeof window !== 'undefined') {
        const localVid = localStorage.getItem('pareja_visitor_id') || sessionStorage.getItem('pareja_visitor_id');
        if (localVid && blockedVids.includes(localVid)) return true;
      }
    }

    return false;
  }, [
    settings.blockedIps,
    settings.blockedVisitorIds,
    effectiveVisitorIp,
    cachedVisitorIp,
    currentVisitor?.visitorId,
    isAdminAuthenticated,
  ]);

  // Associated blocked entry metadata (reason, blockedAt, device, etc.)
  const currentBlockedEntry = useMemo<BlockedIpEntry | undefined>(() => {
    if (!effectiveVisitorIp || !settings.blockedIpEntries) return undefined;
    if (Array.isArray(settings.blockedIpEntries)) {
      return (settings.blockedIpEntries as BlockedIpEntry[]).find((e) => e.ip === effectiveVisitorIp);
    }
    const cleanKey = effectiveVisitorIp.replace(/[^a-zA-Z0-9]/g, '_');
    return (
      settings.blockedIpEntries[cleanKey] ||
      (Object.values(settings.blockedIpEntries) as BlockedIpEntry[]).find((e) => e.ip === effectiveVisitorIp)
    );
  }, [effectiveVisitorIp, settings.blockedIpEntries]);

  // Silence all music / audio immediately if visitor is blocked
  useEffect(() => {
    if (isCurrentVisitorBlocked) {
      try {
        musicPlayer.pause();
        setIsPlayingMusic(false);
      } catch {}
    }
  }, [isCurrentVisitorBlocked]);

  // Si en la base de datos existía estado previo de appKillSwitch.isEnabled, desactivarlo y limpiarlo automáticamente (solo admin)
  useEffect(() => {
    if (settings.appKillSwitch?.isEnabled && isAdminAuthenticated) {
      saveSettingsToDb({
        ...settings,
        appKillSwitch: {
          ...settings.appKillSwitch,
          isEnabled: false,
        },
      }).catch(() => {});
    }
  }, [settings.appKillSwitch?.isEnabled, isAdminAuthenticated]);

  // Modo Mantenimiento de la Web:
  // Si está activado en settings y el usuario NO es un administrador autenticado y no está en /admin
  const isWebInMaintenance = useMemo(() => {
    const isEnabled = Boolean(
      settings.webMaintenance?.isEnabled ??
      settings.webMaintenance?.maintenanceEnabled ??
      (settings as any).maintenanceEnabled
    );
    if (!isEnabled) return false;
    // Si el administrador ya está autenticado, tiene pase libre para ver la web y el panel
    if (isAdminAuthenticated) return false;
    // Si está intentando entrar al panel admin (/admin) o recuperación, se le permite
    if (activeTab === 'admin' || activeTab === 'reset-password') return false;
    return true;
  }, [
    settings.webMaintenance?.isEnabled,
    settings.webMaintenance?.maintenanceEnabled,
    (settings as any).maintenanceEnabled,
    isAdminAuthenticated,
    activeTab,
  ]);

  // Silenciar música si la web está en modo mantenimiento para el visitante
  useEffect(() => {
    if (isWebInMaintenance) {
      try {
        musicPlayer.pause();
        setIsPlayingMusic(false);
      } catch {}
    }
  }, [isWebInMaintenance]);

  // Sincronización instantánea de modo mantenimiento entre pestañas abiertas
  useEffect(() => {
    try {
      const bc = new BroadcastChannel('pareja_web_maintenance_channel');
      bc.onmessage = (event) => {
        if (event.data?.maintenance) {
          setSettings((prev) => ({
            ...prev,
            webMaintenance: event.data.maintenance,
          }));
        }
      };
      return () => bc.close();
    } catch {}
  }, []);

  // Emergency Admin Unlock: allows the legitimate site owner to restore access via Firebase Auth
  const handleEmergencyAdminUnlock = async (email: string, password: string): Promise<boolean> => {
    const res = await signInAdmin(email.trim(), password.trim());
    if (!res.success || !res.user) {
      return false;
    }
    try {
      if (effectiveVisitorIp) {
        await handleUnblockIp(effectiveVisitorIp);
      }
      try {
        localStorage.removeItem('pareja_last_blocked_ip');
      } catch {}
      handleAdminAuthSuccess('novio');
      handleNavigate('admin');
      showNotice('Identidad de administrador verificada con Firebase Auth. Acceso restaurado.', 'success');
      return true;
    } catch (err) {
      console.error('Error in emergency unlock:', err);
      return false;
    }
  };

  // Handler: Block visitor IP in Firestore settings with INSTANT optimistic update (0ms)
  const handleBlockIp = async (
    ip: string,
    meta?: {
      reason?: string;
      deviceModel?: string;
      visitorId?: string;
      country?: string;
      city?: string;
      flagEmoji?: string;
    }
  ) => {
    const cleanIp = ip.trim();
    const sanitizedKey = cleanIp.replace(/[^a-zA-Z0-9]/g, '_');
    const now = new Date().toISOString();
    const newEntry: BlockedIpEntry = {
      ip: cleanIp,
      blockedAt: now,
      reason: meta?.reason || 'Actividad sospechosa detectada',
      deviceModel: meta?.deviceModel || '',
      visitorId: meta?.visitorId || '',
      country: meta?.country || '',
      city: meta?.city || '',
      flagEmoji: meta?.flagEmoji || '',
      blockedBy: activeAdminProfile,
    };

    // 1. INSTANT OPTIMISTIC LOCAL STATE UPDATE (0ms)
    setSettings((prev) => {
      const currentBlocked = Array.isArray(prev.blockedIps) ? [...prev.blockedIps] : [];
      const updatedBlocked = currentBlocked.includes(cleanIp) ? currentBlocked : [...currentBlocked, cleanIp];
      const updatedVisitorIds = meta?.visitorId
        ? [...(prev.blockedVisitorIds || []).filter((v) => v !== meta.visitorId), meta.visitorId]
        : (prev.blockedVisitorIds || []);
      return {
        ...prev,
        blockedIps: updatedBlocked,
        blockedVisitorIds: updatedVisitorIds,
        blockedIpEntries: {
          ...(prev.blockedIpEntries || {}),
          [sanitizedKey]: newEntry,
        },
      };
    });

    // 2. BROADCAST FOR 0ms CROSS-TAB / SAME BROWSER LOCKOUT
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('pareja_last_blocked_ip', cleanIp);
        const bc = new BroadcastChannel('pareja_security_channel');
        bc.postMessage({ type: 'IP_BLOCKED', ip: cleanIp, visitorId: meta?.visitorId });
        bc.close();
      }
    } catch {}

    // 3. PERSIST TO FIRESTORE VIA ATOMIC OPERATION
    try {
      await blockIpAddressInDb(cleanIp, {
        reason: meta?.reason,
        deviceModel: meta?.deviceModel,
        visitorId: meta?.visitorId,
        country: meta?.country,
        city: meta?.city,
        flagEmoji: meta?.flagEmoji,
        blockedBy: activeAdminProfile,
      });
      showNotice(`IP ${cleanIp} bloqueada de forma instantánea.`, 'info');
    } catch (err) {
      console.error('Error blocking IP:', err);
      showNotice('Error al persistir el bloqueo de la IP.', 'danger');
    }
  };

  // Handler: Unblock visitor IP in Firestore settings with INSTANT optimistic update (0ms)
  const handleUnblockIp = async (ip: string, visitorId?: string) => {
    const cleanIp = ip.trim();
    const sanitizedKey = cleanIp.replace(/[^a-zA-Z0-9]/g, '_');

    // 1. INSTANT OPTIMISTIC LOCAL STATE UPDATE (0ms)
    setSettings((prev) => {
      const currentBlocked = Array.isArray(prev.blockedIps) ? [...prev.blockedIps] : [];
      const updatedBlocked = currentBlocked.filter((item) => item !== cleanIp);
      const updatedVisitorIds = visitorId && Array.isArray(prev.blockedVisitorIds)
        ? prev.blockedVisitorIds.filter((v) => v !== visitorId)
        : prev.blockedVisitorIds;
      const updatedEntries = { ...(prev.blockedIpEntries || {}) };
      delete updatedEntries[sanitizedKey];
      return {
        ...prev,
        blockedIps: updatedBlocked,
        blockedVisitorIds: updatedVisitorIds,
        blockedIpEntries: updatedEntries,
      };
    });

    // 2. BROADCAST UNBLOCK
    try {
      if (typeof window !== 'undefined') {
        if (localStorage.getItem('pareja_last_blocked_ip') === cleanIp) {
          localStorage.removeItem('pareja_last_blocked_ip');
        }
        const bc = new BroadcastChannel('pareja_security_channel');
        bc.postMessage({ type: 'IP_UNBLOCKED', ip: cleanIp, visitorId });
        bc.close();
      }
    } catch {}

    // 3. PERSIST TO FIRESTORE
    try {
      await unblockIpAddressInDb(cleanIp, visitorId);
      showNotice(`IP ${cleanIp} desbloqueada exitosamente.`, 'success');
    } catch (err) {
      console.error('Error unblocking IP:', err);
      showNotice('Error al desbloquear la IP.', 'danger');
    }
  };

  // 1. Purge any leftover browser local storage on mount & Subscribe exclusively to Firestore Real-Time
  useEffect(() => {
    clearAllLocalStorageAndIndexedDB();

    // Subscribe to Firestore Settings in Real-Time
    const unsubSettings = subscribeToSettings(
      (newSettings) => {
        setIsFirebaseConnected(true);
        setSettings(newSettings);
        setTimeElapsed(calculateTimeElapsed(newSettings.startDate));
      },
      (err) => {
        console.warn('Error fetching settings:', err);
      }
    );

    // Subscribe to Firestore Memories in Real-Time
    const unsubMemories = subscribeToMemories(
      (newMemories) => {
        setIsFirebaseConnected(true);
        setMemories(newMemories);
      },
      (err) => {
        console.warn('Error fetching memories:', err);
      }
    );

    // Subscribe to Firestore Daily Letters in Real-Time
    const unsubLetters = subscribeToDailyLetters(
      (newLetters) => {
        setIsFirebaseConnected(true);
        setDailyLetters(newLetters);
      },
      (err) => {
        console.warn('Error fetching daily letters:', err);
      }
    );

    // Subscribe to Firestore Music Tracks in Real-Time
    const unsubMusic = subscribeToMusicTracks((tracks) => {
      setSettings((prev) => ({
        ...prev,
        playlist: tracks || [],
      }));
    });

    const handleGardenWateringSync = (e: Event) => {
      const customEvent = e as CustomEvent<GardenWateringState>;
      if (customEvent.detail) {
        setSettings((prev) => ({
          ...prev,
          gardenWatering: {
            ...prev.gardenWatering,
            ...customEvent.detail,
          },
        }));
      }
    };
    window.addEventListener('couple_garden_watering_changed', handleGardenWateringSync);

    return () => {
      window.removeEventListener('couple_garden_watering_changed', handleGardenWateringSync);
      unsubSettings();
      unsubMemories();
      unsubLetters();
      unsubMusic();
    };
  }, []);

  // Subscriptions: visitor telemetry (admin only) and web requests (admin or authenticated user)
  useEffect(() => {
    // 1. Visitor logs: strictly for admin
    let unsubVisitors: () => void = () => {};
    if (isAdminAuthenticated) {
      unsubVisitors = subscribeToVisitorLogs((logs) => {
        if (Array.isArray(logs) && logs.length > 0) {
          setVisitorLogs(logs);
        }
      });
    }

    // 2. Web requests: all requests for admin, or user's own requests for regular applicant
    let unsubWebRequests: () => void = () => {};
    if (isAdminAuthenticated) {
      unsubWebRequests = subscribeToWebRequests((requests) => {
        setIsWebRequestsLoaded(true);
        setWebRequests(requests || []);
      });
    } else if (currentAuthUser?.uid) {
      unsubWebRequests = subscribeToUserWebRequests(currentAuthUser.uid, (requests) => {
        setIsWebRequestsLoaded(true);
        setWebRequests(requests || []);
      });
    } else {
      setWebRequests([]);
      setIsWebRequestsLoaded(true);
    }

    return () => {
      unsubVisitors();
      unsubWebRequests();
    };
  }, [isAdminAuthenticated, currentAuthUser?.uid]);

  // 2. Automatically detect visitor IP, device, and browser, and log to Firestore
  useEffect(() => {
    let isCancelled = false;

    async function initVisitorTracking() {
      try {
        const info = await detectVisitorInfo();
        if (!isCancelled) {
          setCurrentVisitor(info);
          await recordVisitorVisit(info);
        }
      } catch (err) {
        console.warn('Visitor detection status:', err);
      }
    }

    initVisitorTracking();

    return () => {
      isCancelled = true;
    };
  }, []);

  // 3. Real-time Heartbeat for live "Online" status
  useEffect(() => {
    if (!currentVisitor?.docId) return;

    // Send immediate initial heartbeat
    recordVisitorHeartbeat(currentVisitor.docId);

    // Heartbeat every 20 seconds while page is active
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        recordVisitorHeartbeat(currentVisitor.docId);
      }
    }, 20000);

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        recordVisitorHeartbeat(currentVisitor.docId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentVisitor?.docId]);

  // Handler for daily letters update directly in Firestore
  const handleUpdateDailyLetters = async (updated: DailyLetter[]) => {
    if (activeCustomWebRequest) {
      const updatedReq: MyWebRequest = {
        ...activeCustomWebRequest,
        customLetters: updated,
      };
      await handleUpdateCustomWeb(updatedReq);
      return;
    }

    const newIds = new Set(updated.map((l) => l.id));
    const deleted = dailyLetters.filter((l) => !newIds.has(l.id));
    for (const letter of deleted) {
      await deleteDailyLetterFromDb(letter.id);
    }
    for (const letter of updated) {
      await saveDailyLetterToDb(letter);
    }
    setDailyLetters(updated);
  };

  // Synchronize dynamic playlist from effective settings into musicPlayer singleton
  useEffect(() => {
    musicPlayer.setPlaylist(effectiveSettings.playlist || [], effectiveSettings.activeSongId);
  }, [effectiveSettings.playlist, effectiveSettings.activeSongId]);

  // Handler: Background music toggle
  const handleToggleMusic = () => {
    romanticAudio.toggleMusic((playing) => {
      setIsPlayingMusic(playing);
    });
  };

  // Handler: Add new memory (Syncs directly to Firestore cloud)
  const handleSaveMemory = async (
    newMemoryData: Omit<MemoryItem, 'id' | 'comments' | 'createdAt'>
  ) => {
    const newMemory: MemoryItem = {
      ...newMemoryData,
      id: `mem-${Date.now()}`,
      comments: [],
      createdAt: new Date().toISOString(),
    };

    // Optimistic UI update so the memory is immediately visible
    if (activeCustomWebRequest) {
      const updatedMemories = [newMemory, ...effectiveMemories];
      const updatedReq: MyWebRequest = {
        ...activeCustomWebRequest,
        customMemories: updatedMemories,
      };
      await handleUpdateCustomWeb(updatedReq);
      showNotice('¡Recuerdo publicado exitosamente en tu web! 🧡', 'success');
      return;
    }

    setMemories((prev) => [newMemory, ...prev.filter((m) => m.id !== newMemory.id)]);

    try {
      await saveMemoryToDb(newMemory);
      showNotice('¡Recuerdo publicado exitosamente en vuestro álbum! 🧡', 'success');
    } catch (err: any) {
      console.error('Error saving memory to Firestore:', err);
      throw new Error(err?.message || 'Error al guardar en la base de datos');
    }
  };

  // Handler: Delete memory (Deletes directly from Firestore cloud)
  const handleDeleteMemory = async (id: string) => {
    // Optimistic UI update so it immediately vanishes
    setMemories((prev) => prev.filter((m) => m.id !== id));
    try {
      await deleteMemoryFromDb(id);
      romanticAudio.playNote(330, 0.2, 'sine', 0.05);
      showNotice('Recuerdo eliminado del álbum.', 'danger');
    } catch (err) {
      console.error('Error deleting memory from Firestore:', err);
    }
  };

  // Handler: Delete daily letter (Deletes directly from Firestore cloud)
  const handleDeleteDailyLetter = async (id: string) => {
    setDailyLetters((prev) => prev.filter((l) => l.id !== id));
    try {
      await deleteDailyLetterFromDb(id);
      romanticAudio.playChime();
      showNotice('Carta diaria eliminada.', 'danger');
    } catch (err) {
      console.error('Error deleting daily letter from Firestore:', err);
    }
  };

  // Handler: Save settings in Firestore
  const handleSaveSettings = async (newSettings: CoupleSettings) => {
    // 1. Optimistic immediate state update so UI and child components refresh instantaneously
    setSettings(newSettings);
    // 2. Persist to Firestore cloud database
    await saveSettingsToDb(newSettings);
  };

  // Handler: Update memories array in Firestore
  const handleUpdateMemories = async (newMemories: MemoryItem[]) => {
    const newIds = new Set(newMemories.map((m) => m.id));
    const deleted = memories.filter((m) => !newIds.has(m.id));
    for (const mem of deleted) {
      await deleteMemoryFromDb(mem.id);
    }
    for (const mem of newMemories) {
      await saveMemoryToDb(mem);
    }
    setMemories(newMemories);
  };

  // Handler: Import JSON directly to Firestore cloud
  const handleImportData = async (data: {
    settings?: CoupleSettings;
    memories?: MemoryItem[];
    dailyLetters?: DailyLetter[];
    specialEvents?: DailyLetter[];
  }) => {
    if (data.settings) {
      await saveSettingsToDb(data.settings);
    }
    if (data.memories) {
      for (const m of data.memories) {
        await saveMemoryToDb(m);
      }
    }
    const lettersToImport = data.dailyLetters || data.specialEvents;
    if (lettersToImport) {
      for (const l of lettersToImport) {
        await saveDailyLetterToDb(l);
      }
    }
  };

  // Handler: Clean sample data directly in Firestore cloud (Admin only)
  const handleResetDefaultData = async () => {
    try {
      const result = await purgeAllSampleData();
      showNotice(
        `Datos de muestra limpiados (${result.deletedMemories} recuerdos, ${result.deletedLetters} cartas). Las historias reales permanecen intactas.`,
        'success'
      );
    } catch (err: any) {
      showNotice(err?.message || 'Error al procesar la limpieza de datos de muestra.', 'danger');
    }
  };

  // Handler: Create Web Request from public form
  const handleCreateWebRequest = async (
    req: Omit<MyWebRequest, 'id' | 'createdAt'>
  ): Promise<MyWebRequest> => {
    try {
      const user = auth.currentUser || (await signInPublicSession());
      const isAutoApprovalConfigured = Boolean(settings.myWebConfig?.autoApproval);
      const newRequest: MyWebRequest = {
        ...req,
        id: `req_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        createdAt: new Date().toISOString(),
        ownerUid: req.ownerUid || user?.uid,
        status: 'recibido',
      };
      await saveWebRequestToDb(newRequest);
      setWebRequests((prev) => [newRequest, ...prev.filter((r) => r.id !== newRequest.id)]);
      romanticAudio.playBloomSuccess();

      // Validación autoritativa en el backend para aprobación automática
      if (isAutoApprovalConfigured) {
        try {
          const autoRes = await fetch('/api/requests/process-auto-approval', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ requestId: newRequest.id }),
          });
          const autoJson = await autoRes.json().catch(() => null);
          if (autoJson?.processed) {
            newRequest.status = 'aprobado';
            newRequest.approvedAt = new Date().toISOString();
            newRequest.isPublished = true;
          }
        } catch (autoErr) {
          console.warn('Backend auto-approval verification notice:', autoErr);
        }
      }

      showNotice(
        newRequest.status === 'aprobado'
          ? '¡Solicitud aprobada automáticamente! Tu enlace ya está disponible.'
          : '¡Solicitud recibida! El administrador la revisará para su aprobación.',
        'success'
      );
      return newRequest;
    } catch (err) {
      console.error('Error creating web request:', err);
      showNotice('Error al enviar la solicitud. Por favor intenta de nuevo.', 'danger');
      throw err;
    }
  };

  // Handler: Update Web Request Status
  const handleUpdateWebRequestStatus = async (
    id: string,
    status: WebRequestStatus,
    adminNotes?: string,
    extraAdminFields?: Partial<MyWebRequest>
  ) => {
    try {
      await updateWebRequestStatusInDb(id, status, adminNotes, extraAdminFields);
      romanticAudio.playChime();
      const statusLabels: Record<string, string> = {
        recibido: 'marcada como recibida',
        en_revision: 'enviada a revisión',
        cambios_solicitados: 'notificada con cambios requeridos',
        aprobado: 'aprobada',
        publicado: 'publicada con éxito',
        rechazado: 'rechazada',
      };
      showNotice(
        `Solicitud ${statusLabels[status] || status}.`,
        'success'
      );
    } catch (err) {
      console.error('Error updating web request status:', err);
      showNotice('Error al actualizar el estado de la solicitud.', 'danger');
    }
  };

  // Handler: Delete Web Request
  const handleDeleteWebRequest = async (id: string) => {
    try {
      await deleteWebRequestFromDb(id);
      romanticAudio.playChime();
      showNotice('Solicitud eliminada.', 'info');
    } catch (err) {
      console.error('Error deleting web request:', err);
      showNotice('Error al eliminar la solicitud.', 'danger');
    }
  };

  // Handler: Update Custom Web Request (user's personal website data and PIN)
  const handleUpdateCustomWeb = async (updated: MyWebRequest) => {
    try {
      await saveWebRequestToDb(updated);
      setActiveCustomWebRequest(updated);
      setWebRequests((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r))
      );
      romanticAudio.playBloomSuccess();
      showNotice('Cambios de tu web guardados exitosamente.', 'success');
    } catch (err) {
      console.error('Error updating custom web:', err);
      showNotice('Error al guardar los cambios de tu web.', 'danger');
      throw err;
    }
  };

  // 1. DEDICATED PUBLIC APP DOWNLOAD INTERFACE:
  // Rendered immediately for any shared link or download route so anyone can download
  if (activeTab === 'download-app') {
    return (
      <PublicAppDownloadPage
        settings={settings}
        isFirebaseConnected={isFirebaseConnected}
        onUpdateSettings={handleSaveSettings}
      />
    );
  }

  // 1.1. ABSOLUTE GLOBAL LOCKOUT: If the visitor IP is blocked by the admin and not authenticated,
  // THEY CANNOT ACCESS ANYTHING ON THE WEB (Jardín, Álbum, Cartas, Frases, Mi Web, Panel Admin, etc.)
  if (isCurrentVisitorBlocked) {
    return (
      <BlockedIpScreen
        blockedIp={effectiveVisitorIp}
        blockedReason={currentBlockedEntry?.reason}
        blockedEntry={currentBlockedEntry}
        visitorInfo={currentVisitor}
        settings={settings}
        onEmergencyAdminUnlock={handleEmergencyAdminUnlock}
        onRefreshStatus={async () => {
          try {
            const info = await detectVisitorInfo();
            setCurrentVisitor(info);
            setCachedVisitorIp(info.ip);
          } catch {}
        }}
      />
    );
  }


  // 1.3. MODO MANTENIMIENTO WEB:
  // Si el administrador colocó la web en mantenimiento, los visitantes verán la pantalla de mantenimiento
  if (isWebInMaintenance) {
    return (
      <WebMaintenanceScreen
        maintenance={settings.webMaintenance}
        settings={settings}
        onMaintenanceEnded={(updatedSettings) => {
          setSettings(updatedSettings);
        }}
        onNavigateToAdmin={() => handleNavigate('admin')}
      />
    );
  }

  // 1.8. If in /reset-password route or clicked recovery link, render ResetPasswordPage
  if (activeTab === 'reset-password') {
    return (
      <ResetPasswordPage
        onNavigateToLogin={() => handleNavigate('admin')}
        onNavigateToHome={() => handleNavigate('garden')}
      />
    );
  }

  // 2. If in /admin route, render full AdminPage interface
  if (activeTab === 'admin') {
    if (isAuthInitializing) {
      return (
        <div className="min-h-screen bg-[#FFFDFB] flex flex-col items-center justify-center p-4">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200/60 flex items-center justify-center text-rose-500 shadow-sm mb-3">
            <Sparkles className="w-6 h-6 text-rose-500 animate-spin" />
          </div>
          <p className="text-xs font-semibold text-stone-500">Restaurando sesión de administrador...</p>
        </div>
      );
    }

    if (activeCustomWebRequest) {
      if (isUserWebAdminAuth || isAdminAuthenticated) {
        return (
          <UserWebAdminPanel
            request={activeCustomWebRequest}
            onUpdateWeb={handleUpdateCustomWeb}
            onClose={() => setActiveTab('garden')}
            onLogoutPin={() => {
              setUserWebAdminAuth(false);
              setActiveTab('garden');
            }}
          />
        );
      }
      return (
        <div className="min-h-screen bg-[#FFFDFB] flex items-center justify-center p-4">
          <UserWebPinModal
            isOpen={true}
            coupleTitle={`${effectiveSettings.partner1Name} & ${effectiveSettings.partner2Name}`}
            orderCode={activeCustomWebRequest.orderCode}
            partner1Name={effectiveSettings.partner1Name}
            partner2Name={effectiveSettings.partner2Name}
            partner1Email={activeCustomWebRequest.partner1Email || activeCustomWebRequest.email || effectiveSettings.partner1Email}
            partner2Email={activeCustomWebRequest.partner2Email || effectiveSettings.partner2Email}
            assignedPassword={activeCustomWebRequest.assignedPassword}
            requestId={activeCustomWebRequest.id}
            onClose={() => setActiveTab('garden')}
            onSuccess={() => {
              setUserWebAdminAuth(true);
            }}
            onRequestUpdate={async (fields) => {
              const updated = { ...activeCustomWebRequest, ...fields };
              await handleUpdateCustomWeb(updated);
            }}
          />
        </div>
      );
    }

    return (
      <div className="min-h-screen w-full bg-[#FFFDFB] text-[#302B30] font-['Plus_Jakarta_Sans',sans-serif]">
        <AdminPage
          settings={settings}
          memories={memories}
          dailyLetters={dailyLetters}
          specialEvents={dailyLetters}
          visitorLogs={visitorLogs}
          webRequests={webRequests}
          isAuthenticated={isAdminAuthenticated}
          activeAdminProfile={activeAdminProfile}
          currentSection={adminSection}
          onSectionChange={handleAdminSectionChange}
          onLogin={handleAdminAuthSuccess}
          onLogout={handleLogoutAdmin}
          onNavigateToPublic={(route = 'garden') => handleNavigate(route as AppTab)}
          onSaveSettings={handleSaveSettings}
          onUpdateMemories={handleUpdateMemories}
          onUpdateDailyLetters={handleUpdateDailyLetters}
          onUpdateSpecialEvents={handleUpdateDailyLetters}
          onDeleteMemory={handleDeleteMemory}
          onDeleteDailyLetter={handleDeleteDailyLetter}
          onDeleteVisitorLog={deleteVisitorLogFromDb}
          onClearAllVisitorLogs={clearAllVisitorLogsInDb}
          onBlockIp={handleBlockIp}
          onUnblockIp={handleUnblockIp}
          onUpdateWebRequestStatus={handleUpdateWebRequestStatus}
          onUpdateWebRequest={handleUpdateCustomWeb}
          onDeleteWebRequest={handleDeleteWebRequest}
          onOpenUploadModal={() => setIsUploadOpen(true)}
          onImportData={handleImportData}
          onResetDefaultData={handleResetDefaultData}
        />

        {/* Global Upload Modal for quick uploads from Admin */}
        <UploadModal
          isOpen={isUploadOpen}
          settings={settings}
          activeAdminProfile={activeAdminProfile}
          onClose={() => setIsUploadOpen(false)}
          onSaveMemory={handleSaveMemory}
        />
      </div>
    );
  }

  // Not found or deleted personalized couple website screen
  if (customWebNotFound && customWebOrderCode) {
    return (
      <div className="min-h-screen bg-[#FFFDFB] text-[#302B30] flex items-center justify-center p-4 font-sans">
        <div className="max-w-md w-full rounded-3xl bg-white border border-[#F0DDE4] p-6 sm:p-8 text-center space-y-5 shadow-xl">
          <div className="w-16 h-16 rounded-2xl bg-[#FFF4F7] text-[#E85D8E] border border-[#F0DDE4] flex items-center justify-center mx-auto">
            <Sparkles className="w-8 h-8 text-[#E85D8E]" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-bold font-['Playfair_Display',serif] text-[#302B30]">
              Web No Disponible o Eliminada
            </h1>
            <p className="text-xs text-[#77717A] leading-relaxed">
              La página web personalizada con código <code className="font-mono text-[#E83E78] bg-[#FFF4F7] px-1.5 py-0.5 rounded border border-[#F0DDE4]">#{customWebOrderCode}</code> no fue encontrada o fue eliminada por el administrador.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setCustomWebNotFound(false);
              setCustomWebOrderCode(null);
              setActiveCustomWebRequest(null);
              try {
                sessionStorage.removeItem('current_active_web_code');
              } catch {}
              if (typeof window !== 'undefined') {
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, '', cleanUrl);
              }
            }}
            className="w-full py-3 px-5 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all cursor-pointer"
          >
            Ir a la página principal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-full overflow-x-hidden bg-[#f8f9fa] text-slate-800 flex flex-col font-['Plus_Jakarta_Sans',sans-serif]">
      {/* Banner de confirmación de actualización exitosa */}
      {justUpdatedVersion && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92%] p-3.5 rounded-2xl bg-emerald-950/95 border border-emerald-500/60 shadow-2xl backdrop-blur-xl text-white flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
              <Sparkles className="w-4 h-4 text-emerald-300" />
            </div>
            <div>
              <p className="text-xs font-bold text-emerald-200">
                ¡App Actualizada a la Versión v{justUpdatedVersion}!
              </p>
              <p className="text-[11px] text-emerald-300/80">
                Caché purgado con éxito. Acceso directo y completo activado.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setJustUpdatedVersion(null)}
            className="p-1 px-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-emerald-300 text-xs font-bold cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Indicador para el administrador cuando la web está en modo mantenimiento */}
      {isAdminAuthenticated &&
        Boolean(
          settings.webMaintenance?.isEnabled ??
          settings.webMaintenance?.maintenanceEnabled ??
          (settings as any).maintenanceEnabled
        ) && (
        <div className="bg-gradient-to-r from-amber-600 via-amber-700 to-rose-700 text-white px-4 py-2 text-xs font-semibold flex items-center justify-between shadow-md z-40">
          <div className="flex items-center gap-2 max-w-4xl mx-auto w-full justify-between">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span>
                <strong>Modo Mantenimiento Activo</strong>: Los visitantes ven la pantalla de mantenimiento. Tú puedes ver la web porque estás autenticado como administrador.
              </span>
            </span>
            <button
              type="button"
              onClick={() => {
                handleNavigate('admin');
                handleAdminSectionChange('web_settings');
              }}
              className="px-2.5 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-[11px] font-bold text-white transition-all cursor-pointer shrink-0 ml-2"
            >
              Ir a Ajustes Web →
            </button>
          </div>
        </div>
      )}

      {/* FLOATING LOGIN MODAL WITH FIREBASE AUTH */}
      {isUserWebPinModalOpen && activeCustomWebRequest && (
        <UserWebPinModal
          isOpen={true}
          coupleTitle={`${effectiveSettings.partner1Name} & ${effectiveSettings.partner2Name}`}
          orderCode={activeCustomWebRequest.orderCode}
          partner1Name={effectiveSettings.partner1Name}
          partner2Name={effectiveSettings.partner2Name}
          partner1Email={activeCustomWebRequest.partner1Email || activeCustomWebRequest.email || effectiveSettings.partner1Email}
          partner2Email={activeCustomWebRequest.partner2Email || effectiveSettings.partner2Email}
          assignedPassword={activeCustomWebRequest.assignedPassword}
          requestId={activeCustomWebRequest.id}
          onClose={() => setIsUserWebPinModalOpen(false)}
          onSuccess={() => {
            setUserWebAdminAuth(true);
            setIsUserWebPinModalOpen(false);
            setActiveTab('admin');
          }}
          onRequestUpdate={async (fields) => {
            const updated = { ...activeCustomWebRequest, ...fields };
            await handleUpdateCustomWeb(updated);
          }}
        />
      )}

      {/* MINIMALIST ROMANTIC PUBLIC HEADER WITH INTEGRATED PLAYLIST PLAYER */}
      <MinimalistHeader
        activeTab={activeTab}
        settings={effectiveSettings}
        timeElapsed={timeElapsed}
        navVisibility={navVisibility}
        isAdminAuthenticated={isAdminAuthenticated}
        isCustomWeb={Boolean(activeCustomWebRequest)}
        onNavigate={handleNavigate}
        onOpenAdminMusic={() => {
          if (activeCustomWebRequest) {
            handleNavigate('admin');
          } else {
            handleNavigate('admin');
            handleAdminSectionChange('music');
          }
        }}
      />

      {/* MAIN BODY RENDERER */}
      <main className={`flex-1 w-full max-w-full ${activeTab === 'garden' ? 'pb-0 overflow-hidden' : 'pb-32 sm:pb-16'}`}>
        <AnimatePresence mode="wait" initial={false}>
            {activeTab === 'garden' && (
            <motion.div
              key="view-garden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              className="w-full h-full"
            >
              <InteractiveGarden
                settings={effectiveSettings}
                timeElapsed={timeElapsed}
                isAdmin={isAdminAuthenticated}
                onOpenSettings={() => handleNavigate('admin')}
                onGoToBlog={() => handleNavigate('blog')}
                onWaterGarden={(updatedWatering) => {
                  if (updatedWatering) {
                    setSettings((prev) => ({
                      ...prev,
                      gardenWatering: {
                        ...prev.gardenWatering,
                        ...updatedWatering,
                      },
                    }));
                  }
                }}
              />
            </motion.div>
          )}

          {activeTab === 'blog' && (
            <motion.div
              key="view-blog"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="py-4"
            >
              <MediaFeed
                memories={effectiveMemories}
                settings={effectiveSettings}
                currentVisitor={currentVisitor}
                activeAdminProfile={activeAdminProfile}
                onOpenLightbox={(m, idx) => {
                  setLightboxMemory(m);
                  setLightboxInitialIndex(idx ?? 0);
                }}
                onGoToGarden={() => handleNavigate('garden')}
              />
            </motion.div>
          )}

          {activeTab === 'letters' && (
            <motion.div
              key="view-letters"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="py-4"
            >
              <DailyLettersView
                letters={effectiveLetters}
                settings={effectiveSettings}
                isAdmin={isAdminAuthenticated}
                onOpenAdmin={() => handleNavigate('admin')}
                onUpdateLetters={handleUpdateDailyLetters}
                onGoToGarden={() => handleNavigate('garden')}
              />
            </motion.div>
          )}

          {activeTab === 'animations' && (
            <motion.div
              key="view-animations"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
            >
              <RomanticAnimationsView
                settings={effectiveSettings}
                timeElapsed={timeElapsed}
                onGoToGarden={() => handleNavigate('garden')}
                onGoToBlog={() => handleNavigate('blog')}
              />
            </motion.div>
          )}

          {activeTab === 'myweb' && !activeCustomWebRequest && (
            <motion.div
              key="view-myweb"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="py-4"
            >
              <MyWebView
                settings={settings}
                currentVisitor={currentVisitor}
                requests={webRequests}
                currentUser={currentAuthUser}
                isAdminAuthenticated={isAdminAuthenticated}
                onSubmitRequest={handleCreateWebRequest}
                onUpdateRequest={async (id, data) => {
                    if (data.status === 'en_revision') {
                      await submitUserChangesForWebRequest(id, data);
                    } else {
                      await updateUserWebRequestInDb(id, data);
                    }
                    setWebRequests((prev) =>
                      prev.map((r) => (r.id === id ? { ...r, ...data, updatedAt: new Date().toISOString() } : r))
                    );
                  }}
                  onGoToGarden={() => handleNavigate('garden')}
                  onOpenPersonalizedWeb={(req) => setActiveCustomWebRequest(req)}
                />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* MOBILE FLOATING INNOVATIVE DOCK NAVIGATION */}
      <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 sm:hidden w-[94%] max-w-[390px] pb-[env(safe-area-inset-bottom,0px)] pointer-events-none">
        <nav
          className="relative bg-white/95 backdrop-blur-2xl border border-[#F0DDE4] rounded-3xl p-1.5 flex items-center justify-between gap-1 pointer-events-auto shadow-[0_12px_36px_rgba(232,93,142,0.16),0_2px_8px_rgba(0,0,0,0.04)]"
          id="mobile-bottom-nav"
        >
          {[
            {
              id: 'garden' as const,
              label: 'Jardín',
              icon: Flower2,
              visible: navVisibility.garden,
              gradient: 'from-[#FFF4F7] to-[#FFE8F0] text-[#E83E78] border border-[#F0DDE4]',
              glow: 'shadow-[0_2px_10px_rgba(232,93,142,0.15)]',
              badgeColor: 'bg-[#E83E78]',
            },
            {
              id: 'blog' as const,
              label: 'Galería',
              icon: Camera,
              visible: navVisibility.blog,
              gradient: 'from-[#FFF4F7] to-[#FFE8F0] text-[#E83E78] border border-[#F0DDE4]',
              glow: 'shadow-[0_2px_10px_rgba(232,93,142,0.15)]',
              badgeColor: 'bg-[#E83E78]',
            },
            {
              id: 'letters' as const,
              label: 'Cartas',
              icon: Mail,
              visible: navVisibility.letters,
              gradient: 'from-[#FFF4F7] to-[#FFE8F0] text-[#E83E78] border border-[#F0DDE4]',
              glow: 'shadow-[0_2px_10px_rgba(232,93,142,0.15)]',
              badgeColor: 'bg-[#E83E78]',
            },
            {
              id: 'animations' as const,
              label: 'Frases',
              icon: Quote,
              iconClass: 'rotate-180',
              visible: navVisibility.animations,
              gradient: 'from-[#FFF4F7] to-[#FFE8F0] text-[#E83E78] border border-[#F0DDE4]',
              glow: 'shadow-[0_2px_10px_rgba(232,93,142,0.15)]',
              badgeColor: 'bg-[#E83E78]',
            },
            {
              id: 'myweb' as const,
              label: 'Mi Web',
              icon: Globe,
              visible: !activeCustomWebRequest && navVisibility.myWeb !== false && navVisibility.myweb !== false,
              gradient: 'from-[#FFF4F7] to-[#FFE8F0] text-[#E83E78] border border-[#F0DDE4]',
              glow: 'shadow-[0_2px_10px_rgba(232,93,142,0.15)]',
              badgeColor: 'bg-[#E83E78]',
            },
            ...(activeCustomWebRequest
              ? [
                  {
                    id: 'admin' as const,
                    label: 'Mi Admin',
                    icon: KeyRound,
                    visible: true,
                    gradient: 'from-[#FFF4F7] to-[#FFE8F0] text-[#E83E78] border border-[#F0DDE4]',
                    glow: 'shadow-[0_2px_10px_rgba(232,93,142,0.15)]',
                    badgeColor: 'bg-[#E83E78]',
                  },
                ]
              : []),
          ]
            .filter((item) => item.visible)
            .map((item) => {
              const isActive = activeTab === item.id;
              const Icon = item.icon;

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className="relative flex-1 min-w-0 min-h-[48px] flex flex-col items-center justify-center py-1.5 px-1 rounded-2xl cursor-pointer select-none group focus:outline-none"
                  id={`mobile-tab-${item.id}`}
                >
                  {/* Pastilla deslizante animada para pestaña activa */}
                  {isActive && (
                    <motion.div
                      layoutId="mobile-dock-active-pill"
                      className={`absolute inset-0 rounded-2xl bg-gradient-to-tr ${item.gradient} ${item.glow} -z-0`}
                      transition={{
                        type: 'spring',
                        stiffness: 420,
                        damping: 34,
                      }}
                    />
                  )}

                  {/* Micro-indicador luminoso superior */}
                  {isActive && (
                    <motion.div
                      layoutId="mobile-dock-dot"
                      className={`absolute top-1 w-1.5 h-0.5 rounded-full ${item.badgeColor} opacity-90`}
                      transition={{
                        type: 'spring',
                        stiffness: 420,
                        damping: 34,
                      }}
                    />
                  )}

                  {/* Contenido (Icono + Texto) */}
                  <div className="relative z-10 flex flex-col items-center justify-center">
                    <motion.div
                      animate={{
                        scale: isActive ? 1.12 : 1,
                        y: isActive ? -1 : 0,
                      }}
                      transition={{ duration: 0.2 }}
                      className={`transition-colors duration-200 ${
                        isActive
                          ? 'text-[#E83E78]'
                          : 'text-[#77717A] group-hover:text-[#302B30]'
                      }`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${item.iconClass || ''}`} />
                    </motion.div>
                    <span
                      className={`text-[9.5px] mt-1 tracking-tight leading-none truncate max-w-full text-center transition-colors duration-200 ${
                        isActive
                          ? 'text-[#E83E78] font-extrabold'
                          : 'text-[#77717A] font-semibold group-hover:text-[#302B30]'
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                </button>
              );
            })}
        </nav>
      </div>

      {/* PUBLIC FOOTER */}
      {activeTab !== 'garden' && activeTab !== 'animations' && (
        <footer className="relative z-10 bg-gradient-to-b from-transparent via-white/80 to-rose-50/40 border-t border-rose-100/70 pt-10 pb-32 sm:pb-12 px-4 text-center">
          <div className="max-w-md mx-auto flex flex-col items-center">
            {/* Divisor ornamental sutil con corazón */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-rose-300" />
              <div className="w-8 h-8 rounded-full bg-rose-50 border border-rose-200/60 flex items-center justify-center text-rose-500 shadow-xs">
                <Heart className="w-4 h-4 fill-rose-500" />
              </div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-rose-300" />
            </div>

            {/* Nombres de la pareja con diseño elegante y limpio */}
            <h3 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight mb-1.5 flex items-center justify-center gap-2">
              <span>{settings.partner1Name}</span>
              <span className="text-rose-500 font-normal">&amp;</span>
              <span>{settings.partner2Name}</span>
            </h3>

            {/* Frase poética suave */}
            <p className="text-xs text-slate-500 font-normal max-w-xs leading-relaxed mb-3">
              Cada fotografía y cada instante guardado para siempre en nuestro rincón de amor.
            </p>

            {/* Micro-etiqueta delicada */}
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] font-medium text-rose-600/90 bg-rose-50/80 border border-rose-200/50">
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span>Nuestra Historia de Amor</span>
            </div>
          </div>
        </footer>
      )}

      {/* MODALS */}
      <UploadModal
        isOpen={isUploadOpen}
        settings={settings}
        activeAdminProfile={activeAdminProfile}
        onClose={() => setIsUploadOpen(false)}
        onSaveMemory={handleSaveMemory}
      />

      <MediaLightbox
        memory={lightboxMemory}
        initialIndex={lightboxInitialIndex}
        currentVisitor={currentVisitor}
        onClose={() => setLightboxMemory(null)}
      />

      {/* AVISO FLOTANTE DE ACCIÓN (PUBLICAR / ELIMINAR) */}
      <AnimatePresence>
        {toastNotice && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none px-4"
          >
            <div
              className={`px-4 py-3 rounded-2xl shadow-xl border flex items-center gap-3 backdrop-blur-md text-xs sm:text-sm font-semibold pointer-events-auto max-w-md ${
                toastNotice.type === 'danger'
                  ? 'bg-rose-50 text-rose-800 border-rose-200 shadow-rose-500/10'
                  : toastNotice.type === 'success'
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200 shadow-emerald-500/10'
                  : 'bg-white text-[#302B30] border-[#F0DDE4] shadow-pink-500/10'
              }`}
            >
              {toastNotice.type === 'danger' ? (
                <div className="w-7 h-7 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                  <Trash2 className="w-4 h-4" />
                </div>
              ) : toastNotice.type === 'success' ? (
                <div className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
              ) : (
                <div className="w-7 h-7 rounded-xl bg-[#FFF4F7] text-[#E83E78] flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4" />
                </div>
              )}
              <span>{toastNotice.text}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
